import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import UpsertTestKitReceptionOptionForm from 'components/settings/test-kit-reception/UpsertTestKitReceptionOptionForm';
import { FormState } from 'final-form';
import React, { useEffect, useState } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Grid, Modal } from 'semantic-ui-react';
import { getTestKitReceptionOption, searchTestKitReceptionOptions } from 'services/labCentralServices';
import { debounce } from 'ts-debounce';
import { TestKitReceptionOptionDto, TestKitReceptionOptionSearchDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps {
}

interface TestKitReceptionOptionsFieldProps extends FormState {
  values: Partial<TestKitReceptionOptionSearchDto>
}


let searchInitialValues: Partial<TestKitReceptionOptionSearchDto> = {};

const TestKitReceptionOptionsView = (props: Props) => {

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
  const {getMessage, language} = props;

  const [testKitReceptionOptions, setTestKitReceptionOptions] = useState([] as TestKitReceptionOptionDto[]);
  const [testKitReceptionOptionId, setTestKitReceptionOptionId] = useState(0 as number | undefined);
  const [testKitReceptionOption, setTestKitReceptionOption] = useState({} as TestKitReceptionOptionDto | undefined);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [testKitReceptionOptionsLoaded, setTestKitReceptionOptionsLoaded] = useState(false as boolean);
  const [searchValues, setSearchValues] = useState(searchInitialValues as Partial<TestKitReceptionOptionSearchDto>)

  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);


  useEffect(() => {

    searchInitialValues = {
      includesInactive: false
    }

    const fetchData = async () => {
      try {
        await searchTestKitReceptionOptions(searchInitialValues, cancelTokenSource)
            .then(response => {
              setTestKitReceptionOptions(response)
              setTestKitReceptionOptionsLoaded(true)
            })
            .catch((e) => {
              handleError(e.response.data);
            })
      } catch (e) {

      }
    }
    fetchData();
  }, []);

  const fetchTestKitReceptionOptions = (values: any) => {

    setTestKitReceptionOptionsLoaded(false);

    searchTestKitReceptionOptions(values, cancelTokenSource)
        .then(response => {
          setTestKitReceptionOptions(response);
          setSearchValues(values)
        })
        .catch((e) => {
          handleError(e.response.data);
        })
        .finally(() => {
          setTestKitReceptionOptionsLoaded(true)
        })
  }

  const handleChange = debounce(({values}: TestKitReceptionOptionsFieldProps) => {

    setTestKitReceptionOptionsLoaded(false);

    searchTestKitReceptionOptions(values, cancelTokenSource)
        .then(response => {
          setTestKitReceptionOptions(response);
          setSearchValues(values)
        })
        .catch((e) => {
          handleError(e.response.data);
        })
        .finally(() => {
          setTestKitReceptionOptionsLoaded(true)
        })
  }, 300);

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.unknownTestKitReceptionOption
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  }

  const renderReceptionOptionsTableSegment = (): JSX.Element => {

    return (
        <VirtualizedTable
            rowCount={testKitReceptionOptions.length}
            rowGetter={testKitReceptionOptionsRowGetter}
            rowRenderer={testKitReceptionOptionsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 400,
                label: getMessage("labCentral.testKitReceptionOptionsTable.header.description"),
                dataKey: "description",
                cellRenderer: descriptionCellRenderer
              },
              {
                width: 400,
                label: getMessage("labCentral.testKitReceptionOptionsTable.header.status"),
                dataKey: "status",
                cellRenderer: statusCellRenderer
              },
              {
                width: 150,
                label: getMessage("labCentral.testKitReceptionOptionsTable.header.rowActions"),
                dataKey: "id",
                cellRenderer: rowActionCellRenderer
              }
            ]}
        />
    )
  }

  const renderSearchReceptionOptionSegment = (): JSX.Element => {
    return (
        <FinalForm onSubmit={() => {
        }}
                   initialValues={searchInitialValues}
                   subscription={{pristine: true}}
                   render={renderSearchForm}/>
    )
  }

  const renderSearchForm = ({values}: FormRenderProps): React.ReactNode => {

    return (
        <PxGrid>
          {errorMessage &&
              <Grid.Row>
                <Grid.Column width={16}>
                  <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                    {errorMessage}
                  </StyledErrorMessage>
                </Grid.Column>
              </Grid.Row>
          }
          <Grid.Row>
            <Grid.Column width={3} verticalAlign={"middle"}>
              <Field
                  name="includesInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={13}>
              <AddButton buttonMessage={getMessage("button.add")}
                         onClick={showModal}/>
            </Grid.Column>
          </Grid.Row>
          <FormSpy subscription={{values: true}} onChange={handleChange}/>
        </PxGrid>
    )
  }

  const testKitReceptionOptionsRowGetter = ({index}: any) => {
    Object.assign(testKitReceptionOptions[index], {index: index + 1});
    return testKitReceptionOptions[index];
  }

  const testKitReceptionOptionsRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => openAddEditModal(testKitReceptionOptions[index].id)}
        >
          {columns}
        </div>
    );
  };

  const descriptionCellRenderer = ({rowData}: any) => {

    let description = rowData.description;

    if (language === "en" && rowData.descriptionEn !== null) {
      description = rowData.descriptionEn;
    } else if (language === "fr" && rowData.descriptionFr !== null) {
      description = rowData.descriptionFr;
    } else if (language === "it" && rowData.descriptionIt !== null) {
      description = rowData.descriptionIt;
    }

    return description;
  }

  const statusCellRenderer = ({cellData}: any) => {

    if (cellData) {
      return getMessage(`enum.interventionBookingStatus.${cellData}`)
    }
  }

  const rowActionCellRenderer = ({cellData}: any) => {

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => openAddEditModal(cellData)}
          />
        </React.Fragment>
    );
  }

  const openAddEditModal = (id?: number) => {

    if (id) {
      setTestKitReceptionOptionId(id);
      getTestKitReceptionOption(id, cancelTokenSource)
          .then(response => showModal(id, response))
          .catch((e) => {
            handleError(e.response.data);
          })
    }
  }


  const showModal = (id?: number, testKitReceptionOption?: TestKitReceptionOptionDto) => {

    setTestKitReceptionOption(testKitReceptionOption);
    setEditModalOpen(true);

  }

  const hideModal = () => {

    setTestKitReceptionOption(undefined);
    setEditModalOpen(false)
  }

  const saveUpsertForm = () => {
    fetchTestKitReceptionOptions(searchInitialValues);
    hideModal();
  }

  const renderUpsertTestKitReceptionOptionModal = (): React.ReactNode => {

    return (
        <Modal open={editModalOpen}
               closeOnDimmerClick={false}
               onClose={() => hideModal()}>
          <Modal.Header>{getMessage("header.navigation.testKitReception")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertTestKitReceptionOptionForm
                testKitReceptionOptionId={testKitReceptionOptionId}
                testKitReceptionOption={testKitReceptionOption}
                onSave={saveUpsertForm}
                onCancel={hideModal}
            />
          </FormModalContentContainer>
        </Modal>

    );
  }

  return (
      <PraxStyledSegment>
        <div>
          {renderSearchReceptionOptionSegment()}
        </div>

        <StyledViewTableRow>
          {testKitReceptionOptionsLoaded
              ? renderReceptionOptionsTableSegment()
              : <LoaderComponent message={getMessage("loading")}/>

          }
        </StyledViewTableRow>

        {renderUpsertTestKitReceptionOptionModal()}
      </PraxStyledSegment>
  );

}

export default withMessages(withAuthContext(TestKitReceptionOptionsView));
